import {
	HierarchyPosition,
	HierarchyLink,
} from '@components/Hierarchy/BaseHierarchy';
import { ActionItem } from '@components/Hierarchy/ActionHierarchy';

export type AutomationFlowCommandType =
	| 'create_action'
	| 'move_action'
	| 'link_actions'
	| 'remove_action'
	| 'update_action';

export interface BaseCommand {
	type: AutomationFlowCommandType;
}

export interface CreateActionCommand extends BaseCommand {
	type: 'create_action';
	action: ActionItem;
	position: HierarchyPosition;
	animated?: boolean;
}

export interface MoveActionCommand extends BaseCommand {
	type: 'move_action';
	actionId: string;
	position: HierarchyPosition;
	animated?: boolean;
}

export interface LinkActionsCommand extends BaseCommand {
	type: 'link_actions';
	sourceId: string;
	targetId: string;
	linkType?: string;
	color?: string;
	width?: number;
	sourceConnector?: string; // Connector ID on source action (output)
	targetConnector?: string; // Connector ID on target action (input)
}

export interface RemoveActionCommand extends BaseCommand {
	type: 'remove_action';
	actionId: string;
}

export interface UpdateActionCommand extends BaseCommand {
	type: 'update_action';
	actionId: string;
	updates: Partial<ActionItem>;
}

export type AutomationFlowCommand =
	| CreateActionCommand
	| MoveActionCommand
	| LinkActionsCommand
	| RemoveActionCommand
	| UpdateActionCommand;

/**
 * Parse JSON commands from AI streaming response
 * Supports both array of commands and single command
 */
export function parseAutomationFlowCommands(
	data: any
): AutomationFlowCommand[] {
	const commands: AutomationFlowCommand[] = [];

	// Check if commands are in EXTRA object
	if (data?.EXTRA?.automation_flow_commands) {
		const extraCommands =
			data.EXTRA.automation_flow_commands ||
			data.EXTRA.automation_flow_commands;
		if (Array.isArray(extraCommands)) {
			extraCommands.forEach((cmd) => {
				const parsed = parseSingleCommand(cmd);
				if (parsed) commands.push(parsed);
			});
		} else if (extraCommands) {
			const parsed = parseSingleCommand(extraCommands);
			if (parsed) commands.push(parsed);
		}
	}

	// Also check for commands in response text (JSON format)
	if (data?.response && typeof data.response === 'string') {
		const jsonMatches = data.response.match(
			/\{[\s\S]*?"type"\s*:\s*"(create_action|move_action|link_actions|remove_action|update_action)"[\s\S]*?\}/g
		);
		if (jsonMatches) {
			jsonMatches.forEach((match) => {
				try {
					const parsed = JSON.parse(match);
					const cmd = parseSingleCommand(parsed);
					if (cmd) commands.push(cmd);
				} catch (e) {
					console.warn(
						'Failed to parse command from response text:',
						e
					);
				}
			});
		}
	}

	return commands;
}

/**
 * Parse a single command object
 */
function parseSingleCommand(cmd: any): AutomationFlowCommand | null {
	if (!cmd || typeof cmd !== 'object' || !cmd.type) {
		return null;
	}

	try {
		switch (cmd.type) {
			case 'create_action':
				if (!cmd.action || !cmd.position) {
					console.warn(
						'Invalid create_action command: missing action or position'
					);
					return null;
				}
				return {
					type: 'create_action',
					action: {
						_id:
							cmd.action._id ||
							cmd.action.id ||
							`action-${Date.now()}-${Math.random()}`,
						title:
							cmd.action.title ||
							cmd.action.description ||
							'New Action',
						description: cmd.action.description,
						type: cmd.action.type || 'secondary',
						dataSchema: cmd.action.dataSchema,
						connectors: cmd.action.connectors, // Include connectors if provided
						integration: cmd.action.integration, // Include integration if provided
						endpoint: cmd.action.endpoint, // Include endpoint if provided
						...cmd.action,
					},
					position: {
						x:
							typeof cmd.position.x === 'number'
								? cmd.position.x
								: 0,
						y:
							typeof cmd.position.y === 'number'
								? cmd.position.y
								: 0,
					},
					animated: cmd.animated !== false, // Default to true
				} as CreateActionCommand;

			case 'move_action':
				if (!cmd.actionId || !cmd.position) {
					console.warn(
						'Invalid move_action command: missing actionId or position'
					);
					return null;
				}
				return {
					type: 'move_action',
					actionId: String(cmd.actionId),
					position: {
						x:
							typeof cmd.position.x === 'number'
								? cmd.position.x
								: 0,
						y:
							typeof cmd.position.y === 'number'
								? cmd.position.y
								: 0,
					},
					animated: cmd.animated !== false,
				} as MoveActionCommand;

			case 'link_actions':
				if (!cmd.sourceId || !cmd.targetId) {
					console.warn(
						'Invalid link_actions command: missing sourceId or targetId'
					);
					return null;
				}
				return {
					type: 'link_actions',
					sourceId: String(cmd.sourceId),
					targetId: String(cmd.targetId),
					linkType: cmd.linkType || cmd.type || 'sequence',
					color: cmd.color,
					width: cmd.width,
					sourceConnector: cmd.sourceConnector ? String(cmd.sourceConnector) : undefined,
					targetConnector: cmd.targetConnector ? String(cmd.targetConnector) : undefined,
				} as LinkActionsCommand;

			case 'remove_action':
				if (!cmd.actionId) {
					console.warn(
						'Invalid remove_action command: missing actionId'
					);
					return null;
				}
				return {
					type: 'remove_action',
					actionId: String(cmd.actionId),
				} as RemoveActionCommand;

			case 'update_action':
				if (!cmd.actionId || !cmd.updates) {
					console.warn(
						'Invalid update_action command: missing actionId or updates'
					);
					return null;
				}
				return {
					type: 'update_action',
					actionId: String(cmd.actionId),
					updates: cmd.updates,
				} as UpdateActionCommand;

			default:
				console.warn(`Unknown command type: ${cmd.type}`);
				return null;
		}
	} catch (error) {
		console.error('Error parsing command:', error, cmd);
		return null;
	}
}

/**
 * Validate command structure
 */
export function validateCommand(cmd: AutomationFlowCommand): boolean {
	if (!cmd || !cmd.type) return false;

	switch (cmd.type) {
		case 'create_action':
			return !!(cmd.action && cmd.position && cmd.action._id);
		case 'move_action':
			return !!(cmd.actionId && cmd.position);
		case 'link_actions':
			return !!(cmd.sourceId && cmd.targetId);
		case 'remove_action':
			return !!cmd.actionId;
		case 'update_action':
			return !!(cmd.actionId && cmd.updates);
		default:
			return false;
	}
}

/**
 * Extract commands from text response (for fallback parsing)
 */
export function extractCommandsFromText(text: string): AutomationFlowCommand[] {
	const commands: AutomationFlowCommand[] = [];

	// Look for JSON-like command structures in text
	const commandPattern =
		/\{[\s\S]*?"type"\s*:\s*"(create_action|move_action|link_actions|remove_action|update_action)"[\s\S]*?\}/g;
	const matches = text.match(commandPattern);

	if (matches) {
		matches.forEach((match) => {
			try {
				const parsed = JSON.parse(match);
				const cmd = parseSingleCommand(parsed);
				if (cmd && validateCommand(cmd)) {
					commands.push(cmd);
				}
			} catch (e) {
				// Not valid JSON, skip
			}
		});
	}

	return commands;
}
